import React, { useState, useEffect } from 'react';
import { Volume2, VolumeX, Sparkles, Maximize2, Loader2, Radio, Layers, User, Compass, Cpu, Video } from 'lucide-react';
import { ArtworkCue } from '../../types/tome';
import {
  getNarrationController,
  generateArtworkWithGoogleAI,
  loadGeneratedArtCache,
  generateSceneCommentary,
  loadCommentaryCache,
} from '../../services/googleGenAI';
import {
  getArtworkStyleMeta,
} from '../../services/proceduralArt';
import {
  getVisualForPage,
  VisualEntry,
  VISUAL_REPRESENTATIONS,
} from '../../services/visualRegistry';
import { VisualCodexModal } from './VisualCodexModal';
import { VideoMotionCanvas } from './VideoMotionCanvas';

export interface ArtworkCardProps {
  artwork?: ArtworkCue;
  pageNumber: number;
  textParagraphs: string[];
  theme?: string;
  speaker?: string;
}

export const ArtworkCard: React.FC<ArtworkCardProps> = ({
  artwork,
  pageNumber,
  textParagraphs,
  speaker: _speaker,
}) => {
  const [cachedImg, setCachedImg] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isNarrating, setIsNarrating] = useState(false);
  const [isLightbox, setIsLightbox] = useState(false);
  const [isCodexOpen, setIsCodexOpen] = useState(false);
  const [customVisual, setCustomVisual] = useState<VisualEntry | null>(null);
  const [isVideoMode, setIsVideoMode] = useState(true);
  const [commentary, setCommentary] = useState<string | null>(null);
  const [isLoadingCommentary, setIsLoadingCommentary] = useState(false);
  const [showCommentary, setShowCommentary] = useState(false);

  // Subscribe to narration state
  useEffect(() => {
    const controller = getNarrationController();
    const unsub = controller.subscribe((speaking) => {
      setIsNarrating(speaking);
    });
    return unsub;
  }, []);

  // Check generated cache, commentary cache, and reset custom visual on page flip
  useEffect(() => {
    const cache = loadGeneratedArtCache();
    if (cache[pageNumber]) {
      setCachedImg(cache[pageNumber]);
    } else {
      setCachedImg(null);
    }
    const commCache = loadCommentaryCache();
    if (commCache[pageNumber]) {
      setCommentary(commCache[pageNumber]);
    } else {
      setCommentary(null);
    }
    setShowCommentary(false);
    setCustomVisual(null);
  }, [pageNumber]);

  if (!artwork && !customVisual) return null;

  const defaultVisual = getVisualForPage(pageNumber);
  const activeVisual = customVisual || defaultVisual;
  const styleMeta = getArtworkStyleMeta(activeVisual.style);

  const displayImage =
    cachedImg ||
    customVisual?.heroImage ||
    artwork?.generatedImageUrl ||
    activeVisual.heroImage ||
    customVisual?.svgDataUri ||
    artwork?.svgDataUri ||
    activeVisual.svgDataUri;

  const handleGenerateAI = async () => {
    setIsGenerating(true);
    try {
      const promptToUse = activeVisual.prompt || artwork?.prompt || '';
      const newImg = await generateArtworkWithGoogleAI(promptToUse, pageNumber);
      setCachedImg(newImg);
    } catch (err) {
      console.warn('[ArtworkCard] AI generation warning:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleToggleNarration = () => {
    const controller = getNarrationController();
    if (isNarrating) {
      controller.stop();
    } else {
      controller.narratePage(textParagraphs);
    }
  };

  const handleToggleCommentary = async () => {
    if (!showCommentary) {
      setShowCommentary(true);
      if (!commentary) {
        setIsLoadingCommentary(true);
        try {
          const log = await generateSceneCommentary(textParagraphs.join(' '), pageNumber);
          setCommentary(log);
        } catch (err) {
          console.warn('[ArtworkCard] Failed to fetch commentary:', err);
        } finally {
          setIsLoadingCommentary(false);
        }
      }
    } else {
      setShowCommentary(false);
    }
  };

  return (
    <>
      <div
        className="caesura-artwork-container"
        data-testid="caesura-artwork-card"
        style={{
          ['--dynamic-glow' as any]: styleMeta.glowRgba,
          ['--dynamic-accent' as any]: styleMeta.accentColor,
        }}
      >
        {/* Dynamic Atmospheric Glow Backdrop */}
        <div
          className="artwork-ambient-glow"
          style={{ background: `radial-gradient(circle at 50% 50%, ${styleMeta.glowRgba}, transparent 75%)` }}
        />

        <div
          className="artwork-card-frame"
          style={{ borderColor: `${styleMeta.accentColor}55` }}
        >
          {/* Main Semi-Animated Artwork Visual or Generative Video Motion */}
          <div className="artwork-image-wrapper">
            {isVideoMode ? (
              <VideoMotionCanvas
                imageUrl={displayImage}
                title={activeVisual.title}
                caption={activeVisual.caption}
                accentColor={styleMeta.accentColor}
                shaderTheme={activeVisual.style}
                isCharacter={!!activeVisual.characterId}
                onExpand={() => setIsLightbox(true)}
              />
            ) : (
              <>
                <img
                  src={displayImage}
                  alt={activeVisual.title}
                  className={`artwork-visual-image ${isGenerating ? 'generating-blur' : ''}`}
                  loading="lazy"
                />

                {/* Seamless Vignette Blending Layer into Reader Card */}
                <div className="artwork-vignette-mask" />

                {/* Scanning HUD Overlay */}
                <div className="artwork-hud-overlay">
                  <div className="hud-corner hud-top-left" style={{ borderColor: styleMeta.accentColor }} />
                  <div className="hud-corner hud-top-right" style={{ borderColor: styleMeta.accentColor }} />
                  <div className="hud-corner hud-bottom-left" style={{ borderColor: styleMeta.accentColor }} />
                  <div className="hud-corner hud-bottom-right" style={{ borderColor: styleMeta.accentColor }} />
                  <div className="hud-scanline" />
                </div>
              </>
            )}

            {/* Loading Indicator */}
            {isGenerating && (
              <div className="artwork-generating-overlay">
                <Loader2 className="generating-spinner" size={28} />
                <span className="generating-text">GOOGLE MULTI-GEN SYNTHESIS...</span>
              </div>
            )}
          </div>

          {/* Quick Perspective & Character Switcher Strip */}
          <div className="artwork-quick-switcher-strip">
            <div className="quick-switcher-left">
              <button
                onClick={() => setCustomVisual(null)}
                className={`quick-pill-btn ${!customVisual ? 'active-pill' : ''}`}
                title="Default Scene Representation"
              >
                <Compass size={11} />
                <span>SCENE</span>
              </button>
              <button
                onClick={() => setCustomVisual(VISUAL_REPRESENTATIONS[0])} // Marcus Neutral
                className={`quick-pill-btn ${customVisual?.characterId === 'marcus_ward' ? 'active-pill' : ''}`}
                title="View Marcus Ward Portrait"
              >
                <User size={11} />
                <span>WARD</span>
              </button>
              <button
                onClick={() => setCustomVisual(VISUAL_REPRESENTATIONS[4])} // Elena Cruz
                className={`quick-pill-btn ${customVisual?.characterId === 'elena_cruz' ? 'active-pill' : ''}`}
                title="View Elena Cruz Portrait"
              >
                <User size={11} />
                <span>CRUZ</span>
              </button>
              <button
                onClick={() => setCustomVisual(VISUAL_REPRESENTATIONS[7])} // Dr. Peter
                className={`quick-pill-btn ${customVisual?.characterId === 'dr_simon_peter' ? 'active-pill' : ''}`}
                title="View Dr. Simon Peter Portrait"
              >
                <User size={11} />
                <span>PETER</span>
              </button>
              <button
                onClick={() => setCustomVisual(VISUAL_REPRESENTATIONS[11])} // Margin Spectator
                className={`quick-pill-btn ${customVisual?.characterId === 'margin_spectator' ? 'active-pill' : ''}`}
                title="View The Margin Spectator"
              >
                <User size={11} />
                <span>SPECTATOR</span>
              </button>
            </div>

            <div className="quick-switcher-right">
              <button
                onClick={() => setIsVideoMode(!isVideoMode)}
                className={`quick-pill-btn video-motion-toggle-pill ${isVideoMode ? 'active-pill' : ''}`}
                title="Toggle Generative Video Motion Engine (60fps Parallax & Camera Drift)"
              >
                <Video size={11} />
                <span>{isVideoMode ? 'VIDEO MOTION [ON]' : 'VIDEO MOTION [OFF]'}</span>
              </button>
              <button
                onClick={() => setIsCodexOpen(true)}
                className="quick-codex-open-btn"
                title="Open Visual Codex (58+ Micro-Animated Archives)"
              >
                <Layers size={11} />
                <span>CODEX (58+)</span>
              </button>
            </div>
          </div>

          {/* Bottom Telemetry & Multi-Gen Action Strip */}
          <div className="artwork-telemetry-bar">
            <div className="artwork-meta-info">
              <span
                className="artwork-style-tag"
                style={{
                  color: styleMeta.accentColor,
                  background: `${styleMeta.accentColor}1a`,
                  borderColor: `${styleMeta.accentColor}44`,
                }}
              >
                {styleMeta.label.toUpperCase()}
              </span>
              <span className="artwork-caption-text">
                {activeVisual.caption}
              </span>
            </div>

            <div className="artwork-action-buttons">
              {/* Live Voice Narration Trigger */}
              <button
                onClick={handleToggleNarration}
                className={`artwork-action-btn ${isNarrating ? 'active-narrating' : ''}`}
                title={isNarrating ? 'Stop Voice Narration' : 'Play Live Voice Narration'}
                aria-label="Toggle Voice Narration"
              >
                {isNarrating ? <VolumeX size={14} /> : <Volume2 size={14} />}
                <span>{isNarrating ? 'STOP' : 'NARRATE'}</span>
                {isNarrating && <Radio size={12} className="waveform-pulse-icon" />}
              </button>

              {/* Google Gen-AI Visual Synthesis */}
              <button
                onClick={handleGenerateAI}
                disabled={isGenerating}
                className="artwork-action-btn ai-sparkle-btn"
                title="Synthesize 8K Cinematic Concept Art with Google GenAI"
                aria-label="Generate Image with Google AI"
              >
                <Sparkles size={14} />
                <span>GOOGLE AI</span>
              </button>

              {/* In-Character Telemetry via Google Gemini */}
              <button
                onClick={handleToggleCommentary}
                className={`artwork-action-btn ${showCommentary ? 'active-narrating' : ''}`}
                title="Consult Marcus Ward Telemetry via Google Gemini"
                aria-label="Toggle Marcus Ward Telemetry"
              >
                <Cpu size={14} />
                <span>{showCommentary ? 'HIDE LOG' : 'WARD LOG'}</span>
              </button>

              {/* Lightbox Expand */}
              <button
                onClick={() => setIsLightbox(true)}
                className="artwork-action-btn"
                title="Expand Artwork View"
                aria-label="Expand Artwork"
              >
                <Maximize2 size={14} />
              </button>
            </div>
          </div>

          {/* Collapsible Marcus Ward Telemetry HUD */}
          {showCommentary && (
            <div
              className="artwork-telemetry-commentary-box"
              style={{
                margin: '8px 12px 12px',
                padding: '10px 14px',
                background: 'rgba(2, 6, 12, 0.94)',
                border: '1px solid rgba(0, 243, 255, 0.35)',
                borderRadius: '6px',
                fontSize: '0.78rem',
                color: '#e2e8f0',
                fontFamily: 'monospace',
                lineHeight: 1.5,
                boxShadow: '0 4px 20px rgba(0, 243, 255, 0.1)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '6px',
                  borderBottom: '1px solid rgba(0, 243, 255, 0.2)',
                  paddingBottom: '4px',
                }}
              >
                <span style={{ color: 'var(--planck-cyan)', fontWeight: 600, letterSpacing: '0.06em' }}>
                  ENGINEER WARD // DIAGNOSTIC LOG // GOOGLE MULTI-GEN
                </span>
                <button
                  onClick={() => {
                    if (commentary) {
                      const controller = getNarrationController();
                      controller.narratePage([commentary]);
                    }
                  }}
                  title="Narrate Engineer Log"
                  style={{
                    background: 'rgba(0, 243, 255, 0.1)',
                    border: '1px solid rgba(0, 243, 255, 0.3)',
                    color: 'var(--planck-cyan)',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '0.7rem',
                  }}
                >
                  <Volume2 size={11} /> NARRATE LOG
                </button>
              </div>
              {isLoadingCommentary ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8' }}>
                  <Loader2 size={14} className="generating-spinner" />
                  <span>Interrogating Planck lattice via Google Gemini...</span>
                </div>
              ) : (
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{commentary}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Lightbox Modal */}
      {isLightbox && (
        <div
          className="artwork-lightbox-overlay"
          onClick={() => setIsLightbox(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="lightbox-content-card"
            onClick={(e) => e.stopPropagation()}
            style={{ borderColor: styleMeta.accentColor }}
          >
            <img src={displayImage} alt={activeVisual.title} className="lightbox-image" />
            <div className="lightbox-footer">
              <div className="lightbox-title-wrap">
                <h3 style={{ color: styleMeta.accentColor }}>{activeVisual.title}</h3>
                <p>{activeVisual.prompt}</p>
                <code style={{ fontSize: '0.7rem', color: '#6b7c96' }}>
                  {activeVisual.caption}
                </code>
              </div>
              <button className="btn-close-lightbox" onClick={() => setIsLightbox(false)}>
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visual Codex Modal (58+ Micro-Animated Representations) */}
      <VisualCodexModal
        isOpen={isCodexOpen}
        onClose={() => setIsCodexOpen(false)}
        currentVisualId={activeVisual.id}
        onSelectVisual={(entry) => setCustomVisual(entry)}
      />
    </>
  );
};
